import { forwardRef } from "react";
import classNames from "classnames";
import { type MediasVariant } from "~/features/navigation/components/TopBar/MediasLogo";
import { userIsPublic, userIsRegistered, userIsSalesman } from "~/domains/user/utils/roles";
import { Box, styled } from "~/common/mui";

const VARIANT_SX = {
  userAnon: { bgcolor: "surface.main", color: "text.secondary" },
  userPlus: { bgcolor: "primaryContainer.main", color: "primaryContainer.contrastText" },
  userBusiness: { bgcolor: "primaryContainer.main", color: "primaryContainer.contrastText" },
} as const;

const CircleRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "50%",
  flex: "none",
  height: 32,
  width: 32,
  [theme.breakpoints.up("desktop-s")]: {
    height: 48,
    width: 48,
  },
}));

type HeaderCircleProps = Omit<JSX.IntrinsicElements["div"], "ref"> & {
  sx?: object;
};

export const HeaderCircle = forwardRef<HTMLDivElement, HeaderCircleProps>(({ className, ...rest }, ref) => {
  return <CircleRoot className={className} ref={ref} {...rest} />;
});

type UserHeaderCircleProps = HeaderCircleProps & {
  variant: Exclude<MediasVariant, "plus-business">;
};

const VARIANT_KEY = {
  general: "userAnon",
  plus: "userPlus",
  business: "userBusiness",
  growth: "userBusiness",
} as const;

export const UserHeaderCircle = forwardRef<HTMLDivElement, UserHeaderCircleProps>(
  ({ variant, className, sx, ...rest }, ref) => {
    const cls = classNames(className);
    return <HeaderCircle className={cls} sx={{ ...VARIANT_SX[VARIANT_KEY[variant]], ...sx }} ref={ref} {...rest} />;
  },
);

export const getMediasVariant = () => {
  // Anonymous - logged out. Go public - logged out when go public is enabled.
  if (userIsPublic()) {
    return "general";
  } else if (userIsRegistered()) {
    return "plus";
  } else if (userIsSalesman()) {
    return "growth";
  } else {
    return "business";
  }
};
